import { type PropsWithChildren, useState } from 'react'
import { Navigate, useOutletContext } from 'react-router-dom'

import { chain, orderBy, uniqBy } from 'lodash'

import { observer } from 'mobx-react'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useNavigation } from 'modules/core/hooks/useNavigation'

import { isManager } from 'modules/employees/lib/isManager'
import { useEnpsFormContext } from 'modules/enps/lib/EnpsFormContext'
import { getRespondentsTypeCount } from 'modules/enps/lib/getRespondentsTypeCount'
import getSelectedFilters from 'modules/enps/lib/getSelectedFilters'
import { AnalyticResults } from 'modules/enps/store/collections/analyticResults'
import type { Cycle } from 'modules/enps/store/models/AnalyticResult'
import type EnpsForm from 'modules/enps/store/models/Form'
import { AnalyticsFilters } from 'modules/surveys/lib/AnalyticsFilters'

import AnalyticsWrapper from './AnalyticsWrapper'
import { AnalyticsContext, Values } from './useAnalytics'

class DataParams {
  [key: string]: number | number[]
}

interface Props {
  form: EnpsForm
}

const EnpsAnalyticsProvider = ({ children, form }: PropsWithChildren<Props>) => {
  const { employee } = useCurrentSession()
  const formId = form.attributes.id
  const analyticResults = useLazyRef(() => new AnalyticResults()).current
  const [cycle, setCycle] = useState<Cycle | null>(null)
  const [requestFilter] = useState<AnalyticsFilters>(new AnalyticsFilters(formId))
  const { analyticsFilter } = useEnpsFormContext()

  const { loading } = useAsyncData(async () => {
    // TODO: this is mutating state unsafely
    getSelectedFilters(analyticsFilter, requestFilter)

    const dataAnalytics: DataParams = {
      surveys_form_id: formId,
    }

    if (requestFilter.locations.length) {
      dataAnalytics.locations = requestFilter.locations
    }

    if (requestFilter.teams.length) {
      dataAnalytics.teams = requestFilter.teams
    }

    const canSeeAll = form.policy.can('editEnpsForm')
    if (!canSeeAll && employee && isManager(employee)) {
      dataAnalytics.manager_id = employee.id
    }

    await analyticResults.fetch({ data: dataAnalytics })

    if (analyticResults.length > 1) {
      throw new Error(
        `AnalyticResults in surveys should only have one result. Survey form id: ${form.id}`
      )
    }

    // TODO: Remove setState inside an effect
    if (form.hasSchedule && !cycle) {
      const cyclesArray = Array.from(analyticResults.cycles)
      const lastCyle = cyclesArray[cyclesArray.length - 1]
      if (lastCyle) {
        setCycle(lastCyle)
      }
    }
  }, [formId, analyticsFilter])

  const filteredResultByCycle = analyticResults.at(0)?.questionGroupsInCycle(cycle) || []

  // Get latest result per participant to avoid duplication from multiple submissions
  const latestFilteredResults =
    chain(filteredResultByCycle)
      // if there are two responses, analytics only shows the last answer per respondent
      .orderBy((item) => [item.created_at, item.answered_at], ['desc', 'desc'])
      .groupBy((item) => item.participant_id)
      .values()
      .map(
        (groups) =>
          orderBy(groups, [(group) => group.answered_at === null, 'answered_at'], ['desc'])[0]
      )
      .compact()
      .value() || []

  const totalResults = uniqBy(filteredResultByCycle, (x) => x.participant_id).length

  // Only count participants who have answered
  const answeredResults =
    chain(filteredResultByCycle)
      .filter((item) => Object.values(item.answers).length > 0)
      // if there are two responses, analytics only shows the last answer per respondent
      .orderBy((item) => [item.created_at, item.answered_at], ['desc', 'desc'])
      .groupBy((item) => item.participant_id)
      .values()
      .map((group) => group[0])
      .compact()
      .value() || []

  const answeredResultsCount = answeredResults.length || 0

  // Calculate promoters and detractors based on the filtered results
  const { promoters, detractors } = getRespondentsTypeCount(answeredResults)

  const values: Values = {
    analyticResults,
    answeredResults,
    answeredResultsCount,
    cycle,
    filteredResults: latestFilteredResults,
    filteredResultsCount: totalResults,
    form,
    loading,
    setCycle,
    promoters,
    detractors,
  }

  return <AnalyticsContext.Provider value={values}>{children}</AnalyticsContext.Provider>
}

const Analytics = () => {
  const { employee } = useCurrentSession()
  const { form } = useOutletContext<{ form: EnpsForm }>()
  const canEdit = form.policy.can('editEnpsForm')
  const showAnalytics = canEdit || isManager(employee)
  const { navigation } = useNavigation()

  if (!showAnalytics) return <Navigate to={navigation.engagement.enps.answers.fullPath()} />

  return (
    <EnpsAnalyticsProvider form={form}>
      <AnalyticsWrapper />
    </EnpsAnalyticsProvider>
  )
}

export default observer(Analytics)
